import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import Message from './Message';
import Loader from './Loader';
import { getAxiosConfig } from '../utils/http';
import { getOrderDetails, payOrderStripe } from '../slices/orderSlice';

const StripePayForm = ({ orderId, stripePromise }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.user?.userInfo || state.userLogin?.userInfo);
  const [initError, setInitError] = useState('');

  // Stripe can call fetchClientSecret multiple times (re-mounts, retries).
  // Cache the in-flight promise + last session id to prevent multiple payable sessions.
  const clientSecretPromiseRef = useRef(null);
  const lastSessionIdRef = useRef('');
  const completedRef = useRef(false);

  useEffect(() => {
    clientSecretPromiseRef.current = null;
    lastSessionIdRef.current = '';
    completedRef.current = false;
  }, [orderId]);

  const fetchClientSecret = useCallback(async () => {
    if (clientSecretPromiseRef.current) return clientSecretPromiseRef.current;

    clientSecretPromiseRef.current = (async () => {
      setInitError('');
      try {
        const state = { user: { userInfo } };
        const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
        const { data } = await axios.post(`/api/orders/${orderId}/stripe/checkout-session`, {}, config);
        const secret = data?.clientSecret;
        const sessionId = data?.id;
        if (sessionId) lastSessionIdRef.current = String(sessionId);
        if (!secret) throw new Error('Missing Stripe client secret');
        return secret;
      } catch (e) {
        // Allow retries after a failure.
        clientSecretPromiseRef.current = null;
        const msg = e?.response?.data?.message || e?.message || 'Failed to initialize Stripe checkout';
        setInitError(String(msg));
        throw e;
      }
    })();

    return clientSecretPromiseRef.current;
  }, [orderId, userInfo]);

  const handleComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;

    const sessionId = String(lastSessionIdRef.current || '').trim();
    if (sessionId) {
      dispatch(payOrderStripe({ orderId, paymentResult: { session_id: sessionId } }));
      return;
    }

    // Fallback: at least refresh order details so UI can reflect webhook-finalized payment.
    dispatch(getOrderDetails(orderId));
  }, [dispatch, orderId]);

  if (!stripePromise) return <Loader />;
  if (initError) return <Message variant="danger">{initError}</Message>;

  return (
    <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret, onComplete: handleComplete }}>
      <EmbeddedCheckout />
    </EmbeddedCheckoutProvider>
  );
};

export default StripePayForm;
